/**
 * Google Tag Manager configuration
 *
 * Single source of truth for the GTM container ID. Read from
 * `NEXT_PUBLIC_GTM_ID` so staging and production can target different
 * containers; falls back to the historical hardcoded production
 * container so deployments without the env var preserve current
 * behavior.
 *
 * Consumed by:
 *   - `lib/site/gtm-boot.ts` — the single `gtm.js` injection path (eager
 *     per-route via `<GtmRouteScope />`, lazy on `brew_*` conversion events).
 *   - `app/_root/root-document.tsx` to gate the global `<ConsentDefault />`
 *     bootstrap.
 *   - `lib/site/gtm.ts` for safety checks (no-op when GTM is disabled).
 */

import { isFlagValueEnabled } from '@/lib/core/env/flags'

const FALLBACK_GTM_ID = 'GTM-TL7LP6SX'

declare global {
  interface Window {
    __brewGtmEnabled?: boolean
  }
}

const RAW_GTM_ID = process.env.NEXT_PUBLIC_GTM_ID
const DEPLOYMENT_ENV =
  process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV
const shouldUseProductionFallback =
  DEPLOYMENT_ENV === 'production' ||
  (DEPLOYMENT_ENV === undefined && process.env.NODE_ENV === 'production')

function resolveGtmContainerId(): string {
  if (typeof RAW_GTM_ID === 'string') {
    return RAW_GTM_ID
  }
  if (shouldUseProductionFallback) {
    return FALLBACK_GTM_ID
  }
  return ''
}

/**
 * The GTM container ID to use at runtime. Empty string if explicitly
 * disabled via `NEXT_PUBLIC_GTM_ID=""` (e.g. preview/test deploys).
 */
export const GTM_CONTAINER_ID: string = resolveGtmContainerId()

/**
 * Whether the runtime is using the hardcoded production fallback because
 * `NEXT_PUBLIC_GTM_ID` was unset. Useful to surface in dev / staging so a
 * misconfigured preview deploy does not silently pollute the prod
 * container.
 */
export const isUsingFallbackGtmContainer: boolean =
  shouldUseProductionFallback && typeof RAW_GTM_ID !== 'string'

/**
 * Whether the GTM script should be mounted on the page. We require a
 * `GTM-` prefixed value so accidental `G-XXX` (GA4 measurement ID)
 * configuration doesn't load the wrong script tag.
 */
export function isGtmEnabled(): boolean {
  // Client bundles cannot reliably read Vercel's server-only `VERCEL_ENV`.
  // The server-rendered consent/bootstrap script is therefore authoritative in
  // the browser, preventing a production-mode preview build from falling back
  // to the production container inside event helpers.
  if (typeof window !== 'undefined') {
    return window.__brewGtmEnabled === true
  }
  return GTM_CONTAINER_ID.length > 0 && GTM_CONTAINER_ID.startsWith('GTM-')
}

/**
 * One-line operator-friendly summary of the GTM boot configuration.
 * Logged once on the client in non-production builds via
 * `<GtmBootDiagnostics />` so engineers can spot when a preview deploy
 * is unintentionally pointing at the production container.
 *
 * Production NODE_ENV builds skip the log entirely (no value, only noise
 * in the user's devtools), so this stays opt-in by environment.
 */
export function getGtmBootDiagnostics(): string {
  return [
    '[GTM] container=' + (GTM_CONTAINER_ID || '(disabled)'),
    'enabled=' + isGtmEnabled(),
    'usingFallback=' + isUsingFallbackGtmContainer,
    `debug=${isFlagValueEnabled(process.env.NEXT_PUBLIC_GTM_DEBUG)}`,
  ].join(' ')
}
